import { FC, useCallback, useMemo, useState } from 'react';
import { TooltipProps } from 'recharts';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography } from '@altermaind/web/ui';
import { Tabs } from '@/_shared/components';
import { Charts } from '@/_shared/components/Charts';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getSurveyCampaignByIdResponsesResultsStatsCall } from '../../state';

const CustomTooltip = ({ payload }: TooltipProps<number, string>) => {
  const t = useTranslations('surveys.details.stats.tabDepartments.overallBox');

  return (
    <Box
      style={{
        backgroundColor: '#fff',
        color: '#000',
        borderRadius: '8px',
        padding: '6px 10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        fontSize: '14px',
        border: 'none',
      }}
    >
      <Typography mb='xxs'>
        {t('tooltipValue')}{' '}
        <Typography as='span' fontWeight={500}>
          {payload && payload.length ? payload[0].payload.label : 'N/A'}
        </Typography>
      </Typography>
      <Typography>
        {t('tooltipTotal')}{' '}
        <Typography as='span' fontWeight={500}>
          {payload && payload.length ? payload[0].payload.total : 0}
        </Typography>
      </Typography>
    </Box>
  );
};

export const OverallBox: FC = () => {
  const t = useTranslations('surveys.details.stats.tabDepartments');
  const { departmentsStats } = getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();

  const [tabs, setTabs] = useState(() => {
    const allMetrics =
      departmentsStats
        ?.flatMap(department => department.metricsScore || [])
        .filter(metric => metric.metric?.id != null) || [];

    const uniqueMetrics = Array.from(
      new Map(
        allMetrics.map(metric => [metric && metric.metric && metric.metric.id, metric]),
      ).values(),
    );

    return uniqueMetrics.map((metric, index) => ({
      title: metric.metric?.name || '',
      index: typeof metric.metric?.id === 'number' ? metric.metric?.id : index,
      active: index === 0,
      dataTestId: metric.metric?.id,
    }));
  });

  const tabActive = useMemo(() => {
    return tabs.find(tab => tab.active === true);
  }, [tabs]);

  const departmentWithBestScore = useMemo(() => {
    if (!tabActive) return null;
    const metricId = tabActive.index;

    const departmentsWithScores =
      departmentsStats
        ?.map(department => {
          const metric = department?.metricsScore?.find(
            (m, index) =>
              (typeof m.metric?.id === 'number' && m.metric.id === metricId) || index === metricId,
          );
          return metric ? { departmentName: department.departmentName, score: metric.score } : null;
        })
        .filter(Boolean) || [];

    if (departmentsWithScores.length === 0) return null;

    const bestDepartment = departmentsWithScores.reduce((prev, curr) => {
      if (!curr || typeof curr.score !== 'number') return prev;
      if (!prev || typeof prev.score !== 'number') return curr;
      return curr.score > prev.score ? curr : prev;
    });

    return bestDepartment;
  }, [departmentsStats, tabActive]);

  const departmentWithLowerScore = useMemo(() => {
    if (!tabActive) return null;
    const metricId = tabActive.index;

    const departmentsWithScores =
      departmentsStats
        ?.map(department => {
          const metric = department?.metricsScore?.find(
            (m, index) =>
              (typeof m.metric?.id === 'number' && m.metric.id === metricId) || index === metricId,
          );
          return metric ? { departmentName: department.departmentName, score: metric.score } : null;
        })
        .filter(Boolean) || [];

    if (departmentsWithScores.length === 0) return null;

    const bestDepartment = departmentsWithScores.reduce((prev, curr) => {
      if (!curr || typeof curr.score !== 'number') return prev;
      if (!prev || typeof prev.score !== 'number') return curr;
      return curr.score < prev.score ? curr : prev;
    });

    return bestDepartment;
  }, [departmentsStats, tabActive]);

  const setOpenTab = useCallback(
    (index: number) => {
      setTabs([...tabs.map(tab => ({ ...tab, active: tab.index === index }))]);
    },
    [tabs],
  );

  return (
    <Box mt='l'>
      <Typography variant='h2' mb='xs' mt='l'>
        {t('overallBox.title')}
      </Typography>
      <Card>
        <Box overflowX={isDesktop ? 'auto' : 'scroll'}>
          <Tabs
            backgroundColor='grey/dark/background'
            buttonVariant='primary'
            setActiveTab={setOpenTab}
            tabs={tabs}
          />
        </Box>
        <Box mb='xl'>
          <Typography variant='h2' mt='m' mb='xs'>
            {t('overallBox.subTitle')}
          </Typography>
          <Typography variant='p3/light'>{t('overallBox.description')}</Typography>
        </Box>
        <Box
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
          mb='l'
        >
          <Box flex='2' mr='s'>
            <Charts.BarChartComponent
              defaultItemsSize={isDesktop ? 6 : 2}
              data={
                departmentsStats?.map(department => ({
                  name: department.departmentName ?? '',
                  value:
                    department.metricsScore?.find(
                      (m, index) =>
                        (typeof m.metric?.id === 'number' &&
                          m.metric.id === (tabActive?.index as number)) ||
                        index === (tabActive?.index as number),
                    )?.score ?? 0,
                  label:
                    department.metricsScore
                      ?.find(
                        (m, index) =>
                          (typeof m.metric?.id === 'number' &&
                            m.metric.id === (tabActive?.index as number)) ||
                          index === (tabActive?.index as number),
                      )
                      ?.score?.toString() ?? '0',
                  total: department.totalCompletedSurveys,
                })) || []
              }
              domain={[0, 7]}
              domainHasInPercentage={false}
              customTooltipBox={CustomTooltip}
            />
            <Typography variant='h2' mb='xs' mt='m'>
              {t('distributionBySkillBox.title')}
            </Typography>
            <Typography variant='p3/light' mb='l'>
              {t('distributionBySkillBox.description')}
            </Typography>
            <Charts.StackedBarChartComponent
              defaultItemsSize={isDesktop ? 6 : 2}
              data={
                departmentsStats?.map(department => {
                  const currentMetric = department?.metricsScore?.find(
                    m => m?.metric?.id === tabActive?.dataTestId,
                  );
                  return {
                    metric: {
                      id: currentMetric?.metric?.id,
                      name: department.departmentName || '',
                    },
                    score: currentMetric?.score,
                    beginningPercentage: currentMetric?.beginningPercentage,
                    intermediatePercentage: currentMetric?.intermediatePercentage,
                    advancedPercentage: currentMetric?.advancedPercentage,
                    total: department.totalCompletedSurveys,
                  };
                }) || []
              }
            />
          </Box>
          <Box
            flex='1'
            mt={genericResponsiveValue({
              breakPoints: { desktop: 'none' },
              defaultValue: 'm',
            })}
          >
            <Card backgroundColor='grey/dark/background'>
              <Box borderBottom='1px solid' borderBottomColor='grey/light/background' pb='s' mb='m'>
                <Box mb='xs'>
                  <Typography variant='h4' mb='xs'>
                    {t('overallBox.highValue.title', { metric: tabActive?.title })}
                  </Typography>
                </Box>
                <Typography variant='h2'>
                  {departmentWithBestScore?.departmentName || 'N/A'}
                </Typography>
                <Box display='flex' flexDirection='row' alignItems='center' mt='s'>
                  <Typography variant='p2/light'>{t('overallBox.highValue.score')}</Typography>
                  <Card ml='xxs' px='xs' py='xxxxs' backgroundColor='primary/inactive'>
                    <Typography variant='p2/light' fontSize='xs'>
                      {departmentWithBestScore?.score || 'N/A'}
                    </Typography>
                  </Card>
                </Box>
              </Box>
              <Box>
                <Box mb='xs'>
                  <Typography variant='h4' mb='xs'>
                    {t('overallBox.lowerValue.title', { metric: tabActive?.title })}
                  </Typography>
                </Box>
                <Typography variant='h2'>
                  {departmentWithLowerScore?.departmentName || 'N/A'}
                </Typography>
                <Box display='flex' flexDirection='row' alignItems='center' mt='s'>
                  <Typography variant='p2/light'>{t('overallBox.lowerValue.score')}</Typography>
                  <Card ml='xxs' px='xs' py='xxxxs' backgroundColor='error/bright'>
                    <Typography variant='p2/light' fontSize='xs'>
                      {departmentWithLowerScore?.score || 'N/A'}
                    </Typography>
                  </Card>
                </Box>
              </Box>
            </Card>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};
